import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, RefreshCw, Save, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, labelCls, cardCls, btnPrimary, timeAgo,
  Toggle, PageShell, PageLoading, EmptyState, IdPicker, useChannelsAndRoles,
} from '@/lib/pageKit';

const DEFAULTS = {
  enabled: false, block_invites: false, block_links: false,
  spam_enabled: false, spam_max_messages: 5, spam_window_seconds: 8,
  max_mentions: 5, banned_words: [], exempt_role_ids: [], exempt_channel_ids: [],
  timeout_after: 3, timeout_minutes: 10, kick_after: 0, strike_window_hours: 24,
};

function Row({ title, hint, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope]">{title}</p>
        {hint && <p className="text-[var(--text-muted)] text-xs font-[Manrope]">{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

function NumField({ label, hint, value, onChange, min = 0 }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input type="number" min={min} value={value} onChange={e => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))} className={inputCls} />
      {hint && <p className="text-[var(--text-muted)] text-[11px] font-[Manrope] mt-1">{hint}</p>}
    </div>
  );
}

function WordChips({ words, onChange }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const parts = draft.split(',').map(w => w.trim().toLowerCase()).filter(Boolean);
    if (parts.length) onChange([...new Set([...words, ...parts])]);
    setDraft('');
  };
  return (
    <div>
      <label className={labelCls}>Banned words</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {words.length === 0 && <span className="text-[var(--text-muted)] text-xs font-[Manrope]">No banned words</span>}
        {words.map(w => (
          <span key={w} className="inline-flex items-center gap-1 text-xs font-[Manrope] px-2 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">
            {w}
            <button type="button" onClick={() => onChange(words.filter(x => x !== w))} aria-label={`Remove ${w}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="Type a word and press Enter (comma-separate for several)"
        className={inputCls}
      />
    </div>
  );
}

export default function AutoMod() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [cfg, setCfg] = useState(DEFAULTS);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { channels, roles } = useChannelsAndRoles(isGlobal ? null : selectedGuildId);

  const load = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    const [c, r] = await Promise.allSettled([
      axios.get(`${API}/api/automod/${selectedGuildId}`, authHeader()),
      axios.get(`${API}/api/automod/${selectedGuildId}/recent`, authHeader()),
    ]);
    if (c.status === 'fulfilled') setCfg({ ...DEFAULTS, ...c.value.data });
    else toast.error(c.reason?.response?.data?.detail || 'Failed to load automod settings');
    if (r.status === 'fulfilled') setRecent(r.value.data.actions || []);
    setLoading(false);
  }, [selectedGuildId, isGlobal]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));
  const num = (k) => (cfg[k] === '' ? 0 : cfg[k]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...cfg };
      ['spam_max_messages', 'spam_window_seconds', 'max_mentions', 'timeout_after', 'timeout_minutes', 'kick_after', 'strike_window_hours']
        .forEach(k => { body[k] = Number(num(k)) || 0; });
      await axios.put(`${API}/api/automod/${selectedGuildId}`, body, authHeader());
      toast.success('AutoMod settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoading />;

  return (
    <PageShell
      icon={ShieldAlert} title="AutoMod" subtitle="Automatically filter spam, invites, links and banned words"
      testId="automod-main" isGlobal={isGlobal} maxW="max-w-4xl"
      actions={
        <button onClick={load} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <div className={`${cardCls} p-5 space-y-4`}>
        <Row title="Enable AutoMod" hint="Master switch for all rules below" checked={cfg.enabled} onChange={v => set('enabled', v)} />
        <Row title="Block Discord invites" checked={cfg.block_invites} onChange={v => set('block_invites', v)} />
        <Row title="Block links" checked={cfg.block_links} onChange={v => set('block_links', v)} />
        <Row title="Spam detection" hint="Too many messages in a short window" checked={cfg.spam_enabled} onChange={v => set('spam_enabled', v)} />
        {cfg.spam_enabled && (
          <div className="grid gap-3 sm:grid-cols-2">
            <NumField label="Max messages" min={1} value={cfg.spam_max_messages} onChange={v => set('spam_max_messages', v)} />
            <NumField label="Window (seconds)" min={1} value={cfg.spam_window_seconds} onChange={v => set('spam_window_seconds', v)} />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <NumField label="Max mentions per message" hint="0 = off" value={cfg.max_mentions} onChange={v => set('max_mentions', v)} />
        </div>
        <WordChips words={cfg.banned_words} onChange={v => set('banned_words', v)} />
      </div>

      <div className={`${cardCls} p-5 space-y-4`}>
        <p className="text-[var(--text-primary)] text-sm font-semibold font-[Outfit]">Punishments</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <NumField label="Timeout after (strikes)" hint="0 = off" value={cfg.timeout_after} onChange={v => set('timeout_after', v)} />
          <NumField label="Timeout length (minutes)" min={1} value={cfg.timeout_minutes} onChange={v => set('timeout_minutes', v)} />
          <NumField label="Kick after (strikes)" hint="0 = off" value={cfg.kick_after} onChange={v => set('kick_after', v)} />
          <NumField label="Strike window (hours)" min={1} hint="Strikes older than this are forgotten" value={cfg.strike_window_hours} onChange={v => set('strike_window_hours', v)} />
        </div>
      </div>

      <div className={`${cardCls} p-5 space-y-4`}>
        <p className="text-[var(--text-primary)] text-sm font-semibold font-[Outfit]">Exemptions</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <IdPicker label="Exempt roles" items={roles} value={cfg.exempt_role_ids} onChange={v => set('exempt_role_ids', v)} placeholder="No exempt roles" />
          <IdPicker label="Exempt channels" items={channels} prefix="#" value={cfg.exempt_channel_ids} onChange={v => set('exempt_channel_ids', v)} placeholder="No exempt channels" />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
        </button>
      </div>

      <div>
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg mb-3">Recent automod actions</h2>
        {recent.length === 0 ? (
          <EmptyState icon={ShieldAlert} title="No automod actions yet" />
        ) : (
          <div className={`${cardCls} overflow-x-auto`}>
            <table className="w-full text-sm font-[Manrope]">
              <thead>
                <tr className="text-left text-[var(--text-muted)] text-xs uppercase tracking-widest border-b border-[var(--border)]">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Rule</th>
                  <th className="px-4 py-3 font-medium">Content</th>
                  <th className="px-4 py-3 font-medium">Punishment</th>
                  <th className="px-4 py-3 font-medium">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {recent.map((a, i) => (
                  <tr key={i} className="hover:bg-[var(--bg-hover)]">
                    <td className="px-4 py-3 text-[var(--text-primary)] whitespace-nowrap">{a.target_username || a.target_id}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{a.rule}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)] max-w-[240px] truncate" title={a.content}>{a.content}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{a.punishment || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)] whitespace-nowrap">{timeAgo(a.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
